import { supabase } from '@/lib/supabase/client';

// ============================================
// CHECKOUT SESSION CREATION
// ============================================

/**
 * Create a Stripe Checkout session for a paid model download.
 * Returns a checkout_url to redirect the user to, or already_purchased: true.
 */
export async function createCheckoutSession(params: {
  modelId: string;
  userId?: string | null;
  successUrl: string;
  cancelUrl: string;
}): Promise<{ checkout_url?: string; already_purchased?: boolean }> {
  const { data, error } = await supabase.functions.invoke(
    'create-checkout-session',
    {
      body: {
        model_id: params.modelId,
        user_id: params.userId ?? null,
        success_url: params.successUrl,
        cancel_url: params.cancelUrl,
      },
    },
  );

  if (error) {
    throw new Error(error.message || 'Failed to create checkout session');
  }

  return data as { checkout_url?: string; already_purchased?: boolean };
}

// ============================================
// PURCHASE QUERIES
// ============================================

/**
 * Check if a logged-in user has already purchased a model.
 * Returns false for unauthenticated users (they must pay each time).
 */
export async function getUserPurchase(
  modelId: string,
  userId: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('purchases')
    .select('id')
    .eq('model_id', modelId)
    .eq('user_id', userId)
    .eq('status', 'completed')
    .maybeSingle();

  if (error) {
    console.error('Error checking purchase:', error);
    return false;
  }

  return !!data;
}

/**
 * Verify a completed Stripe session and return purchase status.
 * Used on the success redirect page to confirm payment before downloading.
 */
export async function verifySessionPurchase(
  stripeSessionId: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('purchases')
    .select('status')
    .eq('stripe_session_id', stripeSessionId)
    .maybeSingle();

  if (error || !data) {
    return false;
  }

  return data.status === 'completed';
}
